import { useState } from 'react';
import { Copy, RefreshCw, Check, Plus, Key, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';

interface ApiKeyCardProps {
  hasApiKey: boolean;
  newlyCreatedKey: string | null;
  onCreateOrRotate: () => Promise<void>;
  onDismissNewKey: () => void;
  isLoading: boolean;
}

export function ApiKeyCard({
  hasApiKey,
  newlyCreatedKey,
  onCreateOrRotate,
  onDismissNewKey,
  isLoading
}: ApiKeyCardProps) {
  const [copied, setCopied] = useState(false);
  const [rotateDialogOpen, setRotateDialogOpen] = useState(false);

  const maskedKey = '••••••••••••••••••••••••••••••••';

  const handleCopy = async () => {
    if (!newlyCreatedKey) return;
    await navigator.clipboard.writeText(newlyCreatedKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRotate = async () => {
    setRotateDialogOpen(false);
    await onCreateOrRotate();
  };

  const handleCreate = async () => {
    await onCreateOrRotate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>API Key</CardTitle>
        <CardDescription>
          Use this key to authenticate API requests to the attribution endpoint
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {newlyCreatedKey ? (
          <div className="space-y-4">
            <Alert variant="default" className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800">Save your API key now</AlertTitle>
              <AlertDescription className="text-amber-700">
                This is the only time you'll see this key. Copy it and store it securely.
              </AlertDescription>
            </Alert>

            <div className="flex gap-2">
              <Input
                value={newlyCreatedKey}
                readOnly
                className="font-mono text-sm"
              />
              <Button variant="outline" size="icon" onClick={handleCopy}>
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>

            <div className="flex justify-end">
              <Button variant="outline" onClick={onDismissNewKey}>
                Done
              </Button>
            </div>
          </div>
        ) : hasApiKey ? (
          <>
            <div className="flex gap-2">
              <Input
                value={maskedKey}
                readOnly
                className="font-mono text-sm text-muted-foreground"
                disabled
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-muted-foreground">
                Include this key in the <code className="text-xs bg-muted px-1 py-0.5 rounded">X-API-Key</code> header
              </p>
              <Dialog open={rotateDialogOpen} onOpenChange={setRotateDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" disabled={isLoading}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                    Rotate Key
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Rotate API Key?</DialogTitle>
                    <DialogDescription>
                      This will invalidate your current API key immediately. Any services
                      using the old key will stop working until updated with the new key.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button onClick={handleRotate} disabled={isLoading}>
                      {isLoading ? 'Rotating...' : 'Rotate Key'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="rounded-full bg-muted p-3 mb-4">
              <Key className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              No API key configured. Create one to start using the attribution API.
            </p>
            <Button onClick={handleCreate} disabled={isLoading}>
              {isLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create API Key
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
